import _get from 'lodash/get'
import React from 'react'
import { useController } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { P, match } from 'ts-pattern'

import { useFlowsServicesResponses } from '@flows-ui/services/FlowServicesResponsesContext'
import { processStepContentElementText } from '@flows-ui/utils/processStepContentElementText'
import type { RadioCardSet as RadioCardSetProps } from '@flows/core/types/stepContentTypes'
import type { FlowValues } from '@flows/core/types/stepTypes'
import { IMAGE } from '@ui-kit/assets/images/index.native'
import PhosphorIcon from '@ui-kit/components/PhosphorIcon'
import Icon from '@ui-kit/components/SvgIcon'
import UiKitRadioCardSet from '@ui-kit/components/formComponents/RadioCardSet'
import type { RadioCardOptionNative } from '@ui-kit/components/formComponents/RadioCardSet/types'

type FlowsOption = RadioCardSetProps<any, any>['options'][number]

const toImage = (source: FlowsOption) =>
  source.icon ? <PhosphorIcon size={24} {...source.icon} /> : source.imagePath

const withImage = (option: RadioCardOptionNative, source: FlowsOption) => {
  const image = toImage(source)
  return image ? { ...option, icon: undefined, image } : option
}

export const RadioCardSet = <TProps extends RadioCardSetProps<any, any>>(
  props: TProps & { flowValues: FlowValues }
) => {
  const { t } = useTranslation()
  const { field, fieldState } = useController({
    name: props.name,
  })
  const servicesResponses = useFlowsServicesResponses()
  const translateFunction = processStepContentElementText(t, {
    flowValues: props.flowValues,
    servicesResponses,
  })

  const formattedOptions = props.options.map((option): RadioCardOptionNative => {
    const title = translateFunction(option.title)
    const subtitle = option.subtitle ? translateFunction(option.subtitle) : undefined
    const description = option.description ? translateFunction(option.description) : undefined
    const topTag = match(option.topTag)
      .with(P.not(P.nullish), (value) => ({
        label: translateFunction(value.label),
      }))
      .otherwise(() => undefined)

    const imgSrc = _get(IMAGE, option.iconPath ?? '')

    return {
      ...option,
      title,
      subtitle,
      description,
      topTag,
      icon: <Icon imgSrc={imgSrc} />,
    }
  })

  return (
    <UiKitRadioCardSet
      options={formattedOptions.map((option, index) => withImage(option, props.options[index]))}
      layout={props.layout}
      variant={props.variant}
      // default value for field.value is ''
      value={!fieldState.isDirty && field.value === '' ? undefined : field.value}
      disabled={props.disabled}
      errorMessage={fieldState.error?.message}
      onValueChange={field.onChange}
      testID={props.name}
    />
  )
}
